import {
  Component,
  ElementRef,
  computed,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { IconComponent } from '../icon/icon.component';

export type MenuItemControl = 'checkbox' | 'radio' | 'toggle';

@Component({
  selector: 'fc-menu-item',
  imports: [IconComponent],
  template: `
    <button
      [disabled]="disabled()"
      [class.disabled]="disabled()"
      [class]="stateClasses()"
      [attr.role]="controlRole()"
      [attr.aria-checked]="ariaChecked()"
      type="button"
      class="flex items-center gap-8 w-full px-12 py-8 border-0 text-sm text-left cursor-pointer rounded-lg disabled:cursor-default disabled:text-secondary-200"
    >
      @switch (control()) {
        @case ('checkbox') {
          <span
            class="flex size-20 shrink-0 items-center justify-center rounded-sm border"
            [class]="checkboxClasses()"
          >
            @if (indeterminate()) {
              <fc-icon icon="icon-remove" color="white" [size]="16" />
            } @else if (checked()) {
              <fc-icon icon="icon-tick" color="white" [size]="16" />
            }
          </span>
        }
        @case ('radio') {
          <fc-icon
            [icon]="
              checked() ? 'icon-radio-button-on' : 'icon-radio-button-off'
            "
            [color]="radioColor()"
            [size]="20"
          />
        }
      }
      <ng-content />
      @if (hasTrailingContent()) {
        <span class="ml-auto flex shrink-0 items-center gap-8">
          @if (shortcut(); as shortcut) {
            <span
              class="text-xs leading-16 whitespace-nowrap"
              [class]="disabled() ? 'text-secondary-200' : 'text-secondary-400'"
            >
              {{ shortcut }}
            </span>
          }
          @if (badge() !== null) {
            <span
              class="flex size-16 items-center justify-center rounded-full text-xs leading-16"
              [class]="
                disabled()
                  ? 'bg-secondary-100 text-secondary-600'
                  : 'bg-primary-600 text-white'
              "
            >
              {{ badge() }}
            </span>
          }
          @if (control() === 'toggle') {
            <span
              class="flex h-16 w-26 items-center rounded-full border px-1"
              [class]="toggleTrackClasses()"
            >
              <span
                class="size-12 rounded-full"
                [class]="checked() ? 'bg-white' : 'bg-secondary-500'"
              ></span>
            </span>
          }
          @if (hasSubmenu()) {
            <fc-icon
              icon="icon-arrow-right"
              [color]="disabled() ? 'secondary200' : 'secondary400'"
              [size]="20"
            />
          }
        </span>
      }
    </button>
  `,
  styles: `
    :host([aria-disabled='true']) ::ng-deep fc-icon {
      background-color: var(--color-secondary-200);
    }
  `,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.pointer-events]': 'disabled() ? "none" : null',
    '[attr.aria-disabled]': 'disabled() ? "true" : null',
    '(click)': 'closeMenu()',
  },
})
export class MenuItemComponent {
  private elementRef = inject(ElementRef<HTMLElement>);
  disabled = input<boolean>(false);
  selected = input<boolean>(false);
  active = input<boolean>(false);
  variant = input<'default' | 'danger'>('default');
  control = input<MenuItemControl | null>(null);
  checked = input<boolean>(false);
  indeterminate = input<boolean>(false);
  shortcut = input<string | null>(null);
  badge = input<number | null>(null);
  hasSubmenu = input<boolean>(false);
  closeOnClick = input<boolean | undefined>(undefined);

  private readonly keepsMenuOpen = computed(() => {
    const control = this.control();
    return control === 'checkbox' || control === 'toggle' || this.hasSubmenu();
  });

  protected readonly controlRole = computed(() => {
    switch (this.control()) {
      case 'checkbox':
      case 'toggle':
        return 'menuitemcheckbox';
      case 'radio':
        return 'menuitemradio';
      default:
        return null;
    }
  });

  protected readonly ariaChecked = computed(() => {
    if (!this.control()) return null;
    if (this.control() === 'checkbox' && this.indeterminate()) return 'mixed';
    return this.checked() ? 'true' : 'false';
  });

  protected readonly hasTrailingContent = computed(
    () =>
      Boolean(this.shortcut()) ||
      this.badge() !== null ||
      this.control() === 'toggle' ||
      this.hasSubmenu(),
  );

  protected readonly stateClasses = computed(() => {
    if (this.selected()) return 'bg-primary-100 text-primary-600';
    if (this.variant() === 'danger') {
      return 'bg-transparent text-error-500 hover:bg-error-50';
    }
    const background = this.active() ? 'bg-secondary-50' : 'bg-transparent';
    return `${background} text-secondary-500 hover:bg-secondary-50 active:bg-secondary-100`;
  });

  protected readonly checkboxClasses = computed(() => {
    if (this.disabled()) return 'border-secondary-100 bg-secondary-50';
    if (this.checked() || this.indeterminate()) {
      return 'border-primary-600 bg-primary-600';
    }
    return 'border-secondary-300 bg-white';
  });

  protected readonly radioColor = computed(() => {
    if (this.disabled()) return 'secondary200';
    return this.checked() ? 'primary600' : 'secondary300';
  });

  protected readonly toggleTrackClasses = computed(() => {
    if (this.checked()) return 'justify-end border-primary-600 bg-primary-600';
    return 'justify-start border-secondary-400 bg-secondary-50';
  });

  protected closeMenu(): void {
    const shouldClose = this.closeOnClick() ?? !this.keepsMenuOpen();
    if (this.disabled() || !shouldClose) {
      return;
    }
    const popover = this.elementRef.nativeElement.closest('[popover]');
    if (popover && typeof (popover as HTMLElement).hidePopover === 'function') {
      (popover as HTMLElement).hidePopover();
    }
  }
}
